import '../../Assets/CSS/App.css'
import * as THREE from 'three';
import { MODEL } from '../THREEJS/3DWebpage/Initial3DSetup';
import { landingDistance } from './planetLanding';
import { planetHidesCard } from './planetOcclusion';
import { RING_RADIUS, stripLayout, windowFit } from './filmStrip';

export function resetImagePositions(imgGroup) {

    const planet = imgGroup.parent;
    const radius = planet.userData.radius;
    // Cards only. The ring also carries its film strip — a child of this group, added *before* the
    // cards now, though nothing here depends on which order — and this loop would hang it on the
    // ring as though it were an artwork: `userData.rad` and `userData.angle` written onto the
    // band, and `updateImagePosition` turning it to face the planet. Nothing else in the scene is
    // a child of an `imgGroup`, so the filter costs nothing and makes the ring's contents a stated
    // rule rather than a fact about when things are added.
    const cards = imgGroup.children.filter(child => child.userData.isImage);
    // The ring's own count, not the count of cards standing here: the strip is cut for the
    // gallery's works and is already hanging on the ring when the first of them lands, so a
    // texture that never arrives has to leave its frame empty rather than shorten the ring under
    // the strip and put every card after it off the window drawn for it. A group that was never
    // given a count — anything that did not come through `AddImages.set` — falls back to its cards.
    const numImages = imgGroup.userData.cardCount || cards.length;

    if (!numImages) return;

    imgGroup.position.set(0, 0, radius * 0.1);
    imgGroup.rotateY(0);

    // Every card is placed from `slot / count` and the strip's frames are drawn at the same angles
    // — see Utils/filmStrip, whose `frameCentreU` is the one expression for them. Putting the ring
    // back on that baseline means the band goes back to it too: the band carries whatever
    // `rotateRing` has turned it by since it was built, and a drag made before the artwork arrived
    // would otherwise be baked in as a permanent offset between every card and its window.
    const band = imgGroup.userData.filmStrip;

    if (band) band.rotation.y = 0;

    // One set of frame numbers for the whole ring, which the cards are then fitted into.
    const layout = stripLayout({ count: numImages, radius });

    cards.forEach((img, i) => {
        // The slot the work occupies on the ring — its index among the gallery's works for this
        // category, not among the cards standing here — so the ring keeps its gaps and every card
        // lands on the window drawn for it.
        const slot = Number.isInteger(img.userData.slot) ? img.userData.slot : i;
        const angle = (slot / numImages) * Math.PI * 2;
        // Both axes are 2r, so the ring is a true circle and every image clears the
        // planet's surface by one radius. `minorRad` was `radius / 1.5` — smaller than
        // the planet itself, so images near the X axis sat inside the sphere and were
        // hidden by it. Keep both above `radius`, or that comes back. The ring's own radius is
        // `RING_RADIUS`, shared with the film strip, which sits just outside it — one radius on
        // every planet, the count's no more.
        const majorRad = radius * RING_RADIUS;
        const minorRad = radius * RING_RADIUS;
        img.userData.rad = { major: majorRad, minor: minorRad };
        img.userData.angle = angle;

        // A card's size is settled here, once, and not by `updateImagePosition` below — which
        // is now called on every wheel notch and every frame of a drag, and would therefore
        // stamp the fit over the hover's `1.2x` (and over the card the pointer is on) each time
        // the ring turned. A card at rest is at `userData.scale`; a hover multiplies it, and
        // nothing else writes it.
        //
        // The number itself is the largest the artwork can be and still sit inside its frame
        // without being cropped — a flat scale before this, which left the works that are not
        // 3:2 with less of their frame than the frame has room for.
        const geometry = img.geometry.parameters;
        const scale = windowFit(geometry.width, geometry.height, layout);
        img.userData.scale = scale;
        img.scale.set(scale, scale, scale);

        updateImagePosition(img, planet);
    });
}

/**
 * Where on its ring a card sits, from its own angle — and where it looks.
 *
 * Position and orientation only: this is the per-frame half of a rotation, so it must not
 * write anything the interaction owns. The size a card is drawn at belongs to `resetImagePositions`
 * and to `hoverImage` (MainScene), and writing it here is what used to undo a hover as the ring
 * turned under it.
 */
export function updateImagePosition(img, planet) {
    const x = img.userData.rad.minor * Math.sin(img.userData.angle);
    const z = img.userData.rad.major * Math.cos(img.userData.angle);
    img.position.set(
        x,
        0,
        z
    );
    img.lookAt(planet.position);
}
//const y = 10;
/* function getX(x) {

    if (x < y) {
        x = y;
    }
    y = x;
    return x;
} */

export function onWindowResize(renderer, camera, el, e) {
    var winHalf;
    if (e) {
        let aspect = (el.clientWidth / el.clientHeight);
        camera.aspect = aspect;
        camera.updateProjectionMatrix();
        let x = window.innerWidth / 2;
        let y = window.innerHeight / 2;
        winHalf = {
            x: x,
            y: y
        }
    }

    
    renderer.setSize(el.clientWidth, el.clientHeight);
    renderer.render(MODEL.scene, camera);

    return winHalf;
}

export function waitForElement(selector, callback) {
    const interval = setInterval(() => {
        const element = document.querySelector(selector);
        if (element) {
            clearInterval(interval);
            callback(element);
        }
    }, 10); // Check every 100ms
}



/**
 * How many works a planet's ring is for, or 0 if it has no ring — which is what decides how much
 * closer than its landed position the camera may come to that ring (Utils/filmStrip's
 * `ringZoomFor`).
 *
 * Read off the scene graph rather than passed in, because a selected card's resting place
 * (Utils/cardLanding, called from MainScene) holds the planet and not the count. The group is the
 * planet's own child, added by `AddImages.setPlanet`.
 */
export function cardCountOf(planet) {
    const group = planet && planet.children.find(child => child.userData.isImgGroup);

    return (group && group.userData.cardCount) || 0;
}

export function setObjectPositionToFOV(object, camera) {

    if (!object) return;

    const isImage = object.userData.isImage;
    let endPosition;
    let fov = (camera.fov * Math.PI) / 360;
    let direction = new THREE.Vector3(0, 0, 1).normalize();
    // Get the camera's world direction        


    if (!isImage) {
        const radius = object.userData.radius;
        // Held clear of the camera's near plane — the two smallest planets are otherwise
        // dropped by the renderer whole, and the arrival looks like a wrong-way landing.
        // The aspect goes with it: the fov is the vertical one, so a portrait viewport is
        // the narrower of the two and only the aspect knows it.
        //
        // The ring is not asked for anything. It is `RING_RADIUS` on every planet, so the landing
        // is the same framing everywhere and the strip hangs just outside it — see
        // MainScene's ring zoom, which is what closes on a crowded ring after the fact.
        const distanceFromCenter = landingDistance(radius, camera.fov, camera.near, camera.aspect);
        endPosition = new THREE.Vector3().copy(object.position).add(direction.multiplyScalar(distanceFromCenter));
    } else {
        const boundingBox = new THREE.Box3().setFromObject(object);
        const size = boundingBox.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        camera.getWorldDirection(direction);
        direction.normalize();
        const cameraAspect = camera.aspect;
        const distance = (maxDim / (2 * Math.tan(fov / 2)) * cameraAspect) / 3.75;
        // Set the new position of the object
        endPosition = new THREE.Vector3().copy(camera.position).add(direction.multiplyScalar(distance));
    }

    console.log('endPosition', endPosition);

    return endPosition;
}

/**
 * Slides the camera along the axis it already holds with `object`, to `distance` from its centre.
 *
 * That is what the ring zoom is: the camera lands on an axis through the planet's centre —
 * `setObjectPositionToFOV` above is what puts it there — and closing on a ring moves along that
 * same line, in and out and never sideways. Taking the direction from where the camera already is
 * keeps the aim as it was, so nothing has to be turned to look at anything again.
 *
 * A camera sitting exactly on the centre has no axis to read. It is left alone rather than moved
 * to a NaN position, which would put the whole scene out of frame with no way back.
 */
export function placeCameraAtDistance(object, camera, distance) {
    const axis = new THREE.Vector3().subVectors(camera.position, object.position);

    if (axis.lengthSq() === 0) return;

    camera.position.copy(object.position).add(axis.normalize().multiplyScalar(distance));
}

/**
 * Where the pointer is, in the canvas-relative space `Raycaster.setFromCamera` reads.
 *
 * Module level rather than nested in `raycast`, because the image ray below needs the same
 * arithmetic and its note — offsetX, not clientX — is the sort of thing that stops being true
 * the moment it is copied.
 */
function getNormalizedMouseCoords(event) {
    const mouse = new THREE.Vector2();
    const rect = MODEL.renderer.domElement.getBoundingClientRect();
    /*
    //must use offsetX and Y if not using window as renderer.domElement, otherwise it will be inacurate
    */
    mouse.x = ((event.offsetX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.offsetY - rect.top) / rect.height) * 2 + 1;
    return mouse;
}

export function raycast(event, targets, callback) {
    const mouse = getNormalizedMouseCoords(event);
    const camera = MODEL.camera;
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, camera);
    raycaster.layers.set(0);
    raycaster.far = 100000;
    raycaster.near = 0.1;
    //debugRaycaster(raycaster);
    const intersects = raycaster.intersectObjects(targets, true);
    let result = null;
    if (intersects.length > 0) {
        result = intersects[0];
    }


    callback(result);
}

// Held at module level so a hover ray, which runs on every mousemove, allocates nothing of
// its own beyond three's own intersection records.
const cardCentre = new THREE.Vector3();

/**
 * `raycast`, aimed at one planet's cards, and refusing any card that planet is standing in
 * front of.
 *
 * The cards are a ring right around the planet — resetImagePositions puts them on a circle of
 * 1.1 radii in the planet's own frame — so from wherever the camera lands, part of the ring is
 * behind the sphere. `raycast` tests the targets it is handed and the planet is never among
 * them, so a hidden card was hit exactly as readily as a visible one: clicking apparently
 * empty planet could open a work standing behind it, and hovering out there scaled up a card
 * nobody could see.
 *
 * Which cards the planet is hiding, and why the test for that is a sphere rather than a look
 * at which way a card faces, is Utils/planetOcclusion's to answer — that module is the rule,
 * and this is the ray and the layer set it needs.
 */
export function raycastImages(event, planet, imgGroup, callback) {

    const mesh = planet.getObjectByName(planet.name + '_mesh');

    if (!mesh || !imgGroup || imgGroup.children.length === 0) {
        callback(null);
        return;
    }

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(getNormalizedMouseCoords(event), MODEL.camera);
    raycaster.layers.set(0);
    raycaster.far = 100000;
    raycaster.near = 0.1;

    const intersects = raycaster.intersectObjects(imgGroup.children, true);

    if (intersects.length === 0) {
        callback(null);
        return;
    }

    const hit = intersects[0];

    // The planet's own sphere is the occluder the target list above cannot contain. A card it
    // stands in front of is not selectable, and nothing happens at all — no hover, no
    // selection.
    const planetCentre = mesh.getWorldPosition(cardCentre);

    if (planetHidesCard(raycaster.ray.origin, hit.point, planetCentre, planet.userData.radius)) {
        callback(null);
        return;
    }

    callback(hit);
}

/** The middle of the frame: what the lobby's reticle marks, and what it aims with. */
const CENTRE_OF_FRAME = new THREE.Vector2(0, 0);

/**
 * The same ray as `raycast`, fired down the middle of the frame rather than at a
 * pointer. The lobby has no cursor to aim with — its reticle is the aim — so both its
 * click and the reticle's own on-target check come through here, and neither has to
 * unpick the canvas-relative arithmetic above.
 */
export function raycastFromCentre(targets, callback) {
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(CENTRE_OF_FRAME, MODEL.camera);
    raycaster.layers.set(0);
    raycaster.far = 100000;
    raycaster.near = 0.1;

    const intersects = raycaster.intersectObjects(targets, true);

    callback(intersects.length > 0 ? intersects[0] : null);
}

export function debugRaycaster(raycaster) {
    const geometry = new THREE.SphereGeometry(1);
    const material = new THREE.MeshBasicMaterial({ color: 0xff0000 });
    const sphere = new THREE.Mesh(geometry, material);
    sphere.position.copy(raycaster.ray.origin);
    MODEL.scene.add(sphere);

    // Optional: show ray direction
    const arrow = new THREE.ArrowHelper(raycaster.ray.direction, raycaster.ray.origin, 5, 0xff0000, 0.1, 0.1);
    MODEL.scene.add(arrow);
}


/*export function loadingManager(manager, params) {
    manager.onStart = function (url, itemsLoaded, itemsTotal) {
        console.log('Started loading file: ' + url + '.\nLoaded ' + itemsLoaded + ' of ' + itemsTotal + ' files.');
    };
    manager.onLoad = function () {
        console.log('All items loaded.');

    };
    manager.onProgress = function (url, itemsLoaded, itemsTotal) {
        console.log('Loading file: ' + url + '.\nLoaded ' + itemsLoaded + ' of ' + itemsTotal + ' files.');
    };
    manager.onError = function (url) {
        console.log('There was an error loading ' + url);
    };
}*/

